import { useEffect, useState } from "react";
import { Calendar, MapPin, Ticket, Users, Check, Loader2, ExternalLink } from "lucide-react";
import MarketingConsentCheckbox from "./MarketingConsentCheckbox";
import PhoneCountryInput from "./PhoneCountryInput";

/**
 * <CommunityBlock /> — renders 5 Phase C block types with a shared visual
 * language and a single RSVP/registration/booking form.
 *
 *   event           — cover image + title + when/where + RSVP button
 *   meetup          — compact variant of event (community meetups)
 *   ticket_booking  — event with paid ticket tiers (Razorpay handled by
 *                     the caller once we wire it up; MVP records a "pending"
 *                     payment submission that the creator can reconcile).
 *   registration    — free registration form (name/email/phone + qty)
 *   subscription    — recurring plan intent (monthly / quarterly / yearly)
 *
 * All submissions POST to the shared `/public/pages/{slug}/{page}/leads`
 * endpoint with an `intent` that identifies which block captured the row
 * (event_rsvp / meetup_rsvp / ticket / registration / subscription). Extra
 * per-intent fields (ticket_tier, ticket_qty, plan_period, amount, payment
 * status, RSVP status) travel inside the free-form `extra` object so the
 * lead schema stays additive.
 */

// -- helpers ------------------------------------------------------------------

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Resolve the public context (bio hub or specific action page) from the
// current window URL so this component stays route-agnostic.
//   /u/{slug}          → bio hub  (page_slug = null)
//   /u/{slug}/{page}   → action page
function publicSlugs() {
  const parts = (typeof window !== "undefined"
    ? window.location.pathname.split("/").filter(Boolean) : []);
  if (parts[0] === "u" && parts.length >= 3) {
    return { creator_slug: parts[1], page_slug: parts[2] };
  }
  if (parts[0] === "u" && parts.length >= 2) {
    return { creator_slug: parts[1], page_slug: null };
  }
  return { creator_slug: null, page_slug: null };
}

// -- form -----------------------------------------------------------------

function SubmissionForm({ block, kind, editable, tier = null, extraBefore = null }) {
  const c = block.config || {};
  const [state, setState] = useState({
    name: "", email: "", phone: "", ticket_qty: 1, plan_period: c.default_period || "monthly",
    // v146.44 — WhatsApp broadcast opt-in, default ON.
    marketing_consent: true,
  });
  const [status, setStatus] = useState("idle");

  const backend = process.env.REACT_APP_BACKEND_URL;
  const testidBase = `${kind}-form-${block.block_id}`;
  const intentMap = {
    event: "event_rsvp",
    meetup: "meetup_rsvp",
    ticket_booking: "ticket",
    registration: "registration",
    subscription: "subscription",
  };

  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    const { creator_slug, page_slug } = publicSlugs();
    if (!creator_slug) return;
    setStatus("submitting");
    try {
      const intent = intentMap[kind] || "lead";
      const extra = {};
      if (kind === "event" || kind === "meetup") {
        extra.rsvp_status = "going";
        extra.guests = Number(state.ticket_qty) || 1;
      }
      if (kind === "ticket_booking") {
        extra.ticket_tier = tier?.name || "General";
        extra.ticket_qty = Number(state.ticket_qty) || 1;
        extra.amount = (Number(tier?.price) || 0) * extra.ticket_qty;
        extra.currency = tier?.currency || c.currency || "INR";
        extra.payment_status = extra.amount > 0 ? "pending" : "free";
      }
      if (kind === "registration") {
        extra.ticket_qty = Number(state.ticket_qty) || 1;
      }
      if (kind === "subscription") {
        extra.plan_period = state.plan_period;
        extra.amount = Number(c[`price_${state.plan_period}`] || c.price || 0);
        extra.currency = c.currency || "INR";
        extra.payment_status = extra.amount > 0 ? "pending" : "free";
      }
      // Bio-hub blocks post to /public/creators/{slug}/leads;
      // action-page blocks keep the per-page endpoint.
      const url = page_slug
        ? `${backend}/api/public/pages/${creator_slug}/${page_slug}/leads`
        : `${backend}/api/public/creators/${creator_slug}/leads`;
      const res = await fetch(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            block_id: block.block_id,
            intent,
            name: state.name || undefined,
            email: state.email || undefined,
            phone: state.phone || undefined,
            marketing_consent: state.marketing_consent,
            extra,
          }),
        },
      );
      if (!res.ok) throw new Error("failed");
      setStatus("ok");
      setState({ ...state, name: "", email: "", phone: "" });
    } catch { setStatus("err"); }
  };

  if (status === "ok") {
    const okMsg = {
      event: "You're on the list — see you there!",
      meetup: "RSVP received — see you at the meetup!",
      ticket_booking: "Booking recorded. The host will confirm payment shortly.",
      registration: "You're registered — check your inbox for details.",
      subscription: "Subscription request received — you'll get an email to complete payment.",
    }[kind] || "Submitted!";
    return (
      <div className="p-3 border-2 border-dashed text-sm font-bold inline-flex items-center gap-2"
        style={{ borderColor: "var(--ah-fg)", color: "var(--ah-fg)" }}
        data-testid={`${testidBase}-ok`}>
        <Check className="w-4 h-4" /> {okMsg}
      </div>
    );
  }

  const qtyLabel = kind === "subscription" ? null
    : (kind === "event" || kind === "meetup") ? "Guests"
    : "Quantity";

  return (
    <form onSubmit={submit} className="space-y-2 mt-3" data-testid={testidBase}>
      {extraBefore}
      <div className="grid grid-cols-2 gap-2">
        <input
          placeholder="Your name" value={state.name} required
          onChange={(e) => setState({ ...state, name: e.target.value })}
          className="border-2 px-2 py-1.5 text-sm bg-transparent col-span-2 sm:col-span-1"
          style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`${testidBase}-name`} />
        <input
          type="email" placeholder="Email" value={state.email} required
          onChange={(e) => setState({ ...state, email: e.target.value })}
          className="border-2 px-2 py-1.5 text-sm bg-transparent col-span-2 sm:col-span-1"
          style={{ borderColor: "var(--ah-fg)" }}
          data-testid={`${testidBase}-email`} />
      </div>
      {/* v147.99.6 — Split country-code + national-number input.
          Default +91 kills the 91-vs-no-91 duplicate customer rows
          the brand's coin ledger used to accumulate. */}
      <PhoneCountryInput
        value={state.phone}
        onChange={(v) => setState({ ...state, phone: v })}
        placeholder="Phone (optional)"
        testidPrefix={`${testidBase}-phone`}
        compact
      />
      <MarketingConsentCheckbox
        checked={state.marketing_consent}
        onChange={(v) => setState({ ...state, marketing_consent: v })}
        testid={`${testidBase}-consent`}
        compact
      />
      {kind === "subscription" && (
        <label className="block text-xs font-bold">
          Billing period
          <select value={state.plan_period}
            onChange={(e) => setState({ ...state, plan_period: e.target.value })}
            className="w-full border-2 px-2 py-1.5 mt-1 text-sm bg-transparent"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`${testidBase}-period`}>
            {["monthly", "quarterly", "yearly"].filter((p) =>
              c[`price_${p}`] !== undefined && c[`price_${p}`] !== null && c[`price_${p}`] !== ""
                ? true : p === (c.default_period || "monthly")
            ).map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
                {c[`price_${p}`] ? ` — ${c.currency || "INR"} ${c[`price_${p}`]}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {qtyLabel && (
        <label className="block text-xs font-bold">
          {qtyLabel}
          <input type="number" min={1} max={20} value={state.ticket_qty}
            onChange={(e) => setState({ ...state, ticket_qty: e.target.value })}
            className="w-full border-2 px-2 py-1.5 mt-1 text-sm bg-transparent"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`${testidBase}-qty`} />
        </label>
      )}
      <button type="submit" disabled={status === "submitting" || editable}
        className="w-full border-2 font-bold text-sm py-2 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
        data-testid={`${testidBase}-submit`}>
        {status === "submitting" && <Loader2 className="w-3 h-3 animate-spin" />}
        {c.cta_label || {
          event: "RSVP", meetup: "Join meetup",
          ticket_booking: tier?.price > 0 ? `Book — ${tier?.currency || c.currency || "INR"} ${(tier.price * (Number(state.ticket_qty)||1)).toFixed(0)}` : "Book",
          registration: "Register",
          subscription: `Subscribe${c[`price_${state.plan_period}`] ? ` — ${c.currency || "INR"} ${c[`price_${state.plan_period}`]}` : ""}`,
        }[kind]}
      </button>
      {status === "err" && (
        <p className="text-xs text-center" style={{ color: "var(--ah-accent)" }}
          data-testid={`${testidBase}-err`}>Could not submit — please try again.</p>
      )}
    </form>
  );
}

// -- venue banner --------------------------------------------------------

function VenueBanner({ block }) {
  const c = block.config || {};
  const venue = c.venue_location; // hydrated by server; falls back to venue_name
  const label = venue?.path_names
    ? venue.path_names.slice(-2).join(", ")
    : (c.venue_name || null);
  if (!label && !c.address) return null;
  return (
    <p className="text-xs inline-flex items-center gap-1 opacity-80"
       data-testid={`community-venue-${block.block_id}`}>
      <MapPin className="w-3 h-3" />
      {c.venue_name || label} {c.address ? `— ${c.address}` : ""}
    </p>
  );
}

function CoverImage({ src, alt, testid }) {
  if (!src) return null;
  return (
    <div className="mb-3 overflow-hidden border-2" style={{ borderColor: "var(--ah-fg)" }}>
      <img src={src} alt={alt || ""}
        className="w-full h-40 sm:h-48 object-cover" data-testid={testid} />
    </div>
  );
}

// -- Event / Meetup ------------------------------------------------------

export function EventBlock({ block, editable, kind = "event", onClick: _onClick }) {
  const c = block.config || {};
  return (
    <div className="ah-block" data-testid={`block-${kind}-${block.block_id}`}>
      <CoverImage src={c.cover_image_url} alt={c.title}
                  testid={`community-cover-${block.block_id}`} />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight">{c.title || (kind === "meetup" ? "Community meetup" : "Upcoming event")}</p>
          <div className="mt-1 space-y-0.5">
            {c.start_at && (
              <p className="text-xs inline-flex items-center gap-1 opacity-80">
                <Calendar className="w-3 h-3" /> {fmtDate(c.start_at)}
                {c.end_at ? ` – ${fmtDate(c.end_at)}` : ""}
              </p>
            )}
            <VenueBanner block={block} />
            {c.capacity ? (
              <p className="text-xs inline-flex items-center gap-1 opacity-70">
                <Users className="w-3 h-3" /> Capacity: {c.capacity}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {c.description && (
        <p className="text-xs mt-2 opacity-80 whitespace-pre-line">{c.description}</p>
      )}
      <SubmissionForm block={block} kind={kind} editable={editable} />
    </div>
  );
}

// -- Ticket Booking ------------------------------------------------------

export function TicketBookingBlock({ block, editable, onClick: _onClick }) {
  const c = block.config || {};
  const tiers = Array.isArray(c.tiers) && c.tiers.length > 0
    ? c.tiers
    : [{ name: "General", price: Number(c.price) || 0, currency: c.currency || "INR" }];
  const [selected, setSelected] = useState(0);
  const tier = tiers[selected] || tiers[0];

  return (
    <div className="ah-block" data-testid={`block-ticket-${block.block_id}`}>
      <CoverImage src={c.cover_image_url} alt={c.title}
                  testid={`ticket-cover-${block.block_id}`} />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight inline-flex items-center gap-1">
            <Ticket className="w-4 h-4" /> {c.title || "Book tickets"}
          </p>
          <div className="mt-1 space-y-0.5">
            {c.start_at && (
              <p className="text-xs inline-flex items-center gap-1 opacity-80">
                <Calendar className="w-3 h-3" /> {fmtDate(c.start_at)}
              </p>
            )}
            <VenueBanner block={block} />
          </div>
        </div>
      </div>
      {c.description && (
        <p className="text-xs mt-2 opacity-80 whitespace-pre-line">{c.description}</p>
      )}
      {/* Tier picker */}
      {tiers.length > 1 && (
        <div className="grid gap-1 mt-3" data-testid={`ticket-tiers-${block.block_id}`}>
          {tiers.map((t, i) => (
            <button
              key={t.name || i} type="button"
              onClick={() => setSelected(i)}
              disabled={editable}
              className={`text-left border-2 px-2 py-1.5 flex items-center gap-2 disabled:opacity-70 ${i === selected ? "font-bold" : ""}`}
              style={{ borderColor: "var(--ah-fg)", background: i === selected ? "var(--ah-accent)" : "transparent", color: i === selected ? "#fff" : "inherit" }}
              data-testid={`ticket-tier-${block.block_id}-${i}`}
            >
              <span className="flex-1 text-sm">{t.name || `Tier ${i + 1}`}</span>
              <span className="text-xs">
                {Number(t.price) > 0
                  ? `${t.currency || c.currency || "INR"} ${t.price}`
                  : "Free"}
              </span>
            </button>
          ))}
        </div>
      )}
      <SubmissionForm block={block} kind="ticket_booking" editable={editable} tier={tier} />
      {tier?.price > 0 && (
        <p className="text-[10px] opacity-60 mt-1" data-testid={`ticket-payment-hint-${block.block_id}`}>
          Payment collected outside the booking form — the host will share a payment link once your request is confirmed.
        </p>
      )}
    </div>
  );
}

// -- Registration -------------------------------------------------------

export function RegistrationBlock({ block, editable, onClick: _onClick }) {
  const c = block.config || {};
  return (
    <div className="ah-block" data-testid={`block-registration-${block.block_id}`}>
      <CoverImage src={c.cover_image_url} alt={c.title}
                  testid={`registration-cover-${block.block_id}`} />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight">{c.title || "Register now"}</p>
          <div className="mt-1 space-y-0.5">
            {c.start_at && (
              <p className="text-xs inline-flex items-center gap-1 opacity-80">
                <Calendar className="w-3 h-3" /> {fmtDate(c.start_at)}
              </p>
            )}
            <VenueBanner block={block} />
            {c.capacity ? (
              <p className="text-xs inline-flex items-center gap-1 opacity-70">
                <Users className="w-3 h-3" /> Capacity: {c.capacity}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {c.description && (
        <p className="text-xs mt-2 opacity-80 whitespace-pre-line">{c.description}</p>
      )}
      <SubmissionForm block={block} kind="registration" editable={editable} />
    </div>
  );
}

// -- Subscription -------------------------------------------------------

export function SubscriptionBlock({ block, editable, onClick: _onClick }) {
  const c = block.config || {};
  const benefits = Array.isArray(c.benefits) ? c.benefits : [];
  return (
    <div className="ah-block" data-testid={`block-subscription-${block.block_id}`}>
      <p className="font-bold text-base leading-tight">{c.title || "Subscribe"}</p>
      {c.tagline && <p className="text-xs opacity-80 mt-0.5">{c.tagline}</p>}
      {benefits.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs" data-testid={`sub-benefits-${block.block_id}`}>
          {benefits.map((b, i) => (
            <li key={i} className="inline-flex items-start gap-1.5">
              <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: "var(--ah-accent)" }} />
              <span>{typeof b === "string" ? b : b.text}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex gap-2 flex-wrap text-xs">
        {["monthly", "quarterly", "yearly"].map((p) => (
          c[`price_${p}`] ? (
            <span key={p} className="border-2 px-1.5 py-0.5"
              style={{ borderColor: "var(--ah-fg)" }}
              data-testid={`sub-price-${p}-${block.block_id}`}>
              {c.currency || "INR"} {c[`price_${p}`]} / {p}
            </span>
          ) : null
        ))}
      </div>
      <SubmissionForm block={block} kind="subscription" editable={editable} />
    </div>
  );
}
